import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import {
  LayoutDashboard, Search, Calculator, ClipboardCheck, Kanban, Wrench,
  Handshake, CalendarDays, Bell, Users, FileText, Settings,
  CheckCircle2, Star, Zap, BookOpen, ClipboardList, ShieldCheck,
  WifiOff, Building2, FileBarChart, Sparkles, Banknote, MapPin, MapPinned
} from 'lucide-react';

const Step = ({ num, children }) => (
  <div className="flex gap-3 items-start">
    <div className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{num}</div>
    <div className="text-sm text-zinc-300 pt-0.5 leading-relaxed">{children}</div>
  </div>
);

const Note = ({ children, kind = 'info' }) => {
  const styles = {
    info: 'bg-blue-500/10 border-blue-500/30 text-blue-300',
    success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
    warn: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
    new: 'bg-purple-500/10 border-purple-500/30 text-purple-300',
  };
  return (
    <div className={`p-3 rounded-lg border text-xs leading-relaxed ${styles[kind]}`} data-testid={`help-note-${kind}`}>
      {children}
    </div>
  );
};

const sections = [
  {
    icon: LayoutDashboard,
    title: "Tableau de bord",
    color: "text-emerald-500",
    description: "Vue d'ensemble + 'Ce qui t'attend aujourd'hui'",
    steps: [
      "KPI temps réel : projets actifs, capital engagé, plus-value brute & nette consolidées.",
      "Section 'Ce qui t'attend aujourd'hui' : BDC à valider (avec montant), RDV du jour (notaire / autres), RDV de la semaine, offres en attente, projets stagnants > 21j, alertes immobilières avec nouvelles annonces, postes travaux en dépassement (>10%).",
      "Chaque carte est cliquable et te dépose directement sur la bonne page filtrée.",
      "Graphiques : répartition projets par statut + écart prévu/réel."
    ]
  },
  {
    icon: Search,
    title: "Recherche de biens",
    color: "text-blue-500",
    description: "Centralise les annonces (IAD, LBC, Coup de cœur, etc.)",
    steps: [
      "Bouton 'Ajouter un bien' → titre, adresse, ville, prix affiché, surface, type, source.",
      "📍 Autocomplétion d'adresse via la Base Adresse Nationale (BAN) : tapez le début de l'adresse, l'app propose les vraies adresses postales avec coordonnées GPS précises (lat/lon) — la ville et le code postal sont remplis automatiquement.",
      "Le prix saisi est le 'prix affiché' (annonce) — le prix négocié sera renseigné plus tard via l'offre d'achat acceptée.",
      "🤖 Dès l'enregistrement, l'app lance automatiquement en arrière-plan une analyse marché complète (cadastre, PLU, risques, ventes DVF) — vous voyez un toast 'Analyse marché lancée…' puis la liste se rafraîchit ~10s plus tard avec un badge vert ✓ sur la carte du bien.",
      "Le bouton 'Analyse marché' sur chaque carte ouvre le dialogue détaillé (5 onglets : Carte, Cadastre, PLU, Risques, Ventes comparables).",
      "Recherche multicritère + filtres source / ville.",
      "Chaque bien affiche des badges cliquables vers : Étude liée, Visite, Projet en cours.",
      "Utilisez les Alertes Immobilières (voir section dédiée) pour aller chercher des biens automatiquement sur 7 plateformes."
    ],
    notes: [
      { kind: 'new', text: <><strong>Auto-analyse marché (NEW) :</strong> à chaque création ou modification d'adresse, 4 APIs gouvernementales sont interrogées en parallèle (~5-15s) et le résultat est persisté sur le bien. Plus besoin de cliquer manuellement sur "Cadastre" comme avant — c'est instantané.</> }
    ]
  },
  {
    icon: MapPinned,
    title: "Analyse marché (Cadastre / PLU / Risques / DVF)",
    color: "text-amber-400",
    description: "Données officielles intégrées pour chaque bien",
    steps: [
      "Bouton 'Analyse marché' sur la carte d'un bien → dialogue avec 5 onglets.",
      "🗺️ Onglet Carte : carte interactive Leaflet (OpenStreetMap) avec marqueur GPS du bien + cercle de 250m matérialisant la zone d'influence immédiate (commerces, transports, voisinage).",
      "📐 Onglet Cadastre : numéro de parcelle, section, surface cadastrale (m²), commune INSEE — issus de l'API IGN apicarto (officiel).",
      "📋 Onglet PLU : zonage urbanistique (U / AU / N / A), libellé long (ex. 'Zone urbaine dense centrale'), date d'approbation du PLU, lien vers le règlement PDF — issu du Géoportail de l'Urbanisme.",
      "⚠️ Onglet Risques : nature des risques majeurs (mouvement de terrain, séisme, sécheresse, inondation, risque industriel...) + historique complet des arrêtés de catastrophe naturelle publiés au JO (date début/fin événement, date arrêté, code national INTE...). Zone sismicité de 1 à 5 si renseignée — source Géorisques.",
      "💰 Onglet Ventes comparables (DVF) : toutes les ventes immobilières des 3 dernières années dans un rayon de 500m, avec prix au m² moyen, top 15 des ventes proches (adresse, date, type, surface, valeur, distance). Source : files.data.gouv.fr/geo-dvf (Etalab officiel)."
    ],
    notes: [
      { kind: 'info', text: <><strong>Cas d'usage banquier :</strong> ces données prouvent objectivement que votre prix d'achat est cohérent avec le marché du quartier, et que la zone est constructible / pas en zone à risque majeur. Toutes ces sections sont intégrées au PDF Dossier de financement (sections 7, 8 et 9).</> },
      { kind: 'warn', text: <><strong>Précision :</strong> plus l'adresse est précise (numéro + rue, validée via autocomplétion BAN), plus l'analyse est fiable. Évitez les "rue de la Paix Paris" sans numéro — préférez "12 rue de la Paix, 75002 Paris".</> }
    ]
  },
  {
    icon: Calculator,
    title: "Étude de rentabilité",
    color: "text-amber-500",
    description: "Calculateur IS complet incluant les frais société",
    steps: [
      "Sélectionne un bien dans le menu déroulant (les infos prix / surface se pré-remplissent).",
      "Coûts d'acquisition : prix d'achat, frais notaire (2% par défaut pour MDB), frais d'agence.",
      "Travaux : estimation HT + marge imprévus + honoraires architecte + dommages-ouvrage + géomètre + diagnostics.",
      "Frais bancaires : dossier, courtage, garantie + assurance emprunteur mensuelle (si emprunt).",
      "Frais de portage : durée prévisionnelle, taux emprunt, PNO, taxe foncière, charges copro.",
      "Revente : prix estimé, frais agence revente, home staging, frais pub, régime TVA (Marge / Total / Exonérée).",
      "📊 Suggestion marché DVF (NEW) : si le bien a une analyse marché disponible, une carte verte apparaît dans la section Revente avec le prix m² moyen du quartier × votre surface + comparaison % avec votre cible actuelle. Bouton 'Appliquer' pour injecter directement la valeur.",
      "Frais fixes société : injectés automatiquement via la quote-part (frais_fixes_mensuels × durée_portage / nb_projets_parallèles). Configure-les dans Paramètres > Frais société.",
      "Bouton '🚀 Lancer ce projet' (visible si PV nette > 0 ET ROI net ≥ 10%) → crée/promeut le projet en 'En cours d'achat' avec budgets pré-remplis.",
      "Export PDF avec page de garde, camembert des coûts, tableau IS détaillé : parfait pour le banquier."
    ],
    notes: [
      {
        kind: 'new',
        text: <>
          <strong>Comprendre les chiffres mensuels (corrigés en mai 2026) :</strong>
          <ul className="mt-2 space-y-1 list-disc list-inside">
            <li><strong>Charges de portage du bien</strong> = ce qui sort du compte tous les mois pendant le projet (intérêts emprunt, assurance emprunteur, PNO/12, TF/12, copro/12).</li>
            <li><strong>Frais fixes société (quote-part)</strong> = part mensuelle imputée à ce projet (compta, assurances, salaires...).</li>
            <li><strong>À provisionner chaque mois</strong> = somme des deux, vraie charge récurrente.</li>
            <li><strong>Enveloppe travaux</strong> = montant total décaissé par lots selon le phasage chantier (acompte 30%, factures intermédiaires, solde). <em>Ce n'est PAS un débit linéaire mensuel</em>. La "cadence moyenne" est juste une indication.</li>
            <li><strong>Trésorerie à mobiliser à l'achat</strong> = apport + frais bancaires + premier acompte travaux (30%). Disponibilité Day 1 du compromis/acte.</li>
          </ul>
        </>
      },
    ]
  },
  {
    icon: BookOpen,
    title: "Bordereau de Prix Unitaire (BPU)",
    color: "text-fuchsia-500",
    description: "Référentiel de prix MO unique par artisan",
    steps: [
      "Page 'Bordereau' = catalogue des prix MO + matériel (fourni-posé) à partager avec tes artisans (118 lignes seedées sur 13 corps de métier — Cher / rural).",
      "Édition inline : libellé, unité (m² / ml / unité / forfait), prix, notes. Ajoute des lignes par catégorie ou un nouveau corps de métier.",
      "Bouton 'Importer depuis vidéo' : upload une vidéo de devis manuscrit ou tableur scanné → l'IA Gemini Vision extrait les lignes (96 lignes en ~2 min) → fusion intelligente (matching par similarité ≥85%).",
      "Export PDF pro avec conditions générales + signature artisan.",
      "Le BPU alimente : l'autocomplete des Visites Techniques, les Bons de Commande pré-remplis, le calcul automatique des coûts."
    ],
    notes: [
      { kind: 'success', text: <><strong>Sync auto :</strong> modifier un prix BPU met à jour les Bons de Commande non-validés où l'artisan n'a pas encore saisi de quantité (qty=0). Les BDC validés ne sont JAMAIS impactés (engagement contractuel).</> }
    ]
  },
  {
    icon: ClipboardCheck,
    title: "Visites techniques",
    color: "text-purple-500",
    description: "Compte-rendu chantier + chiffrage automatique BPU",
    steps: [
      "Bouton 'Nouvelle visite' + sélection du bien (les infos s'affichent).",
      "13 catégories par corps de métier (vides au départ). Pour chaque catégorie : barre de recherche autocomplete sur ton BPU.",
      "Tape 'tirag' → suggestions live → clic → ligne ajoutée avec libellé + unité + prix BPU automatique → tu saisis juste la quantité → coût auto-calculé.",
      "Statut par poste : Conforme / À remettre / Attention / Non vérifié / Remplacer.",
      "Photos par poste : multi-photos avec annotations (camera mobile compatible iOS/Android).",
      "Lignes 'Hors bordereau' pour les prestations non référencées.",
      "Budget total auto-calculé en temps réel (option saisie manuelle disponible).",
      "Verdict : Viable / Non viable / En évaluation.",
      "Sauvegarder → un projet en 'Prospection' est créé automatiquement si aucun n'existe pour le bien.",
      "Bouton 'Rapport PDF' → Rapport Pro (voir section dédiée) prêt pour la négociation."
    ]
  },
  {
    icon: FileBarChart,
    title: "Rapport PDF Pro de Visite (NOUVEAU)",
    color: "text-rose-500",
    description: "Document de négociation premium",
    steps: [
      "Sur la page Visites, bouton 'Rapport PDF' génère un rapport ~1MB.",
      "Page de garde : logo, nom société, titre, infos bien (titre, adresse, surface, prix annoncé, date).",
      "Sommaire en 5 sections.",
      "Pré-qualification : verdict GO / ATTENTION / NO GO calculé sur ratio travaux/prix (≤30% = GO, >60% ou non viable = NO GO, sinon ATTENTION) + 3 KPIs.",
      "Synthèse travaux par corps d'état + camembert.",
      "Détail postes : tableau qty/PU/statut/total + photos annotées (2 par ligne, légendées avec le nom du poste).",
      "Étude de rentabilité synthétique automatiquement injectée si une étude existe pour ce bien.",
      "Notes & observations libres.",
      "Footer 'DOCUMENT CONFIDENTIEL' + numéro de page sur chaque page."
    ],
    notes: [
      { kind: 'info', text: <>Mode legacy (ancien bon de commande artisan) toujours accessible via <code className="bg-zinc-800 px-1.5 py-0.5 rounded">?mode=bdc</code> sur l'URL du PDF.</> }
    ]
  },
  {
    icon: ClipboardList,
    title: "Bons de Commande artisans (BDC)",
    color: "text-blue-400",
    description: "Workflow contractuel avec l'artisan",
    steps: [
      "Auto-création depuis la Visite Technique quand le projet passe en 'Acheté' / 'En rénovation' OU à la création d'un Suivi Travaux : 1 BDC par corps de métier.",
      "Pré-rempli avec les items de la visite (quantités saisies) + TOUTES les autres lignes du BPU du même corps de métier (qty=0). L'artisan n'a qu'à compléter ses quantités.",
      "MO + Fournitures séparées avec totaux HT/TVA/TTC (TVA 0 / 5,5 / 10 / 20%).",
      "Workflow 5 statuts : à remplir artisan → à valider → validé (PDF exportable) / refusé (avec motif) / annulé.",
      "Bouton 'Sync BPU' : ajoute les nouvelles lignes du BPU + MAJ les prix qty=0 sans toucher au travail artisan.",
      "Encart 'Comparatif BDC vs Visite' : pastille colorée 🟢/🟠/🔴 selon écart (≤10% / 10-25% / >25%).",
      "Liste regroupée par projet en accordéons collapsables + toggle 'Masquer les BDC à 0 €'.",
      "Upload facture sur un BDC validé → crée automatiquement la dépense dans le Suivi Travaux et recalcule travaux_reels."
    ]
  },
  {
    icon: Building2,
    title: "Espace Artisan",
    color: "text-cyan-500",
    description: "Portail dédié aux artisans (rôle restreint)",
    steps: [
      "Depuis 'Réseau Artisans', sur chaque carte : bouton 'Créer accès' (badge vert 'Accès actif' une fois fait).",
      "Saisis email + mot de passe → un compte rôle 'artisan' est créé et lié à la fiche.",
      "L'artisan se connecte → il ne voit QUE les BDC qui lui sont assignés.",
      "Sidebar minimaliste (Bons de commande uniquement). Pas de Pipeline, Études, Documents, Pipeline ni voice assistant.",
      "Il peut : saisir ses quantités, ajouter des fournitures, écrire ses notes, dater le démarrage, durée travaux, soumettre, télécharger le PDF validé.",
      "Il NE peut PAS : modifier libellé/unité/prix BPU, supprimer des lignes, valider, refuser, créer des BDC.",
      "Notification admin : chaque BDC soumis (statut 'à valider') apparaît dans la cloche en priorité haute avec montant TTC + nom artisan + lien direct."
    ],
    notes: [
      { kind: 'success', text: <><strong>Sécurité :</strong> middleware backend bloque toute requête API non autorisée pour le rôle artisan, même via une URL connue.</> }
    ]
  },
  {
    icon: Kanban,
    title: "Pipeline des projets",
    color: "text-orange-500",
    description: "9 statuts de la prospection à la vente",
    steps: [
      "9 colonnes : Prospection, En cours d'achat, Compromis signé, Acheté, En rénovation, En vente, Vendu (+ Annulé / En pause selon config).",
      "Drag & drop pour changer de statut.",
      "Auto-création projet : enregistrer une étude crée le projet en 'Prospection'. Une offre envoyée → passage en 'En cours d'achat'. Une offre acceptée → 'Compromis signé' avec prix d'achat réel + date.",
      "Quand un projet passe en 'Acheté', le Suivi Travaux est créé avec budgets pré-remplis depuis la visite.",
      "Boutons 'Compromis' (>= compromis_signe) et 'Acte authentique' (>= achete) sur chaque carte → upload direct + classement auto dans Documents.",
      "Bloc 'Prévu vs Réel' compact : Achat / Travaux / Revente avec écart coloré (rouge si dépassement, vert si économie ou gain).",
      "Pour les projets vendus, plus-value brute & nette calculées automatiquement."
    ]
  },
  {
    icon: Wrench,
    title: "Suivi des travaux",
    color: "text-red-500",
    description: "Budget prévu vs réel par poste, factures, artisans",
    steps: [
      "Auto-créé quand le projet passe en 'Acheté'. Budgets prévus pré-remplis depuis la visite.",
      "Chaque poste : assigner un artisan, fixer le budget prévu, suivre l'avancement (%).",
      "Ajouter chaque dépense réelle : date, montant, description, facture (upload direct caméra mobile ou fichier).",
      "Le budget réel se calcule automatiquement à partir des dépenses.",
      "Écart en vert (économie) ou rouge (dépassement). Alerte dashboard si > 10% de dépassement.",
      "Bouton 'Actualiser' pour resynchro avec la visite si tu l'as modifiée.",
      "PDF comparatif budget prévu / réel exportable."
    ]
  },
  {
    icon: Handshake,
    title: "Offres d'achat",
    color: "text-cyan-400",
    description: "Suivi des offres + sync automatique pipeline",
    steps: [
      "Nouvelle offre → bien, prix proposé, statut, conditions suspensives, notes.",
      "% de réduction calculé automatiquement vs prix affiché.",
      "Sync Pipeline : offre 'Envoyée'/'En négociation' → projet en 'En cours d'achat'. Offre 'Acceptée' → 'Compromis signé' + prix_achat_reel + date. Offre 'Refusée' → retour 'Prospection' uniquement si aucune autre offre active.",
      "Bouton 'Compromis signé' (statut acceptée) → upload direct vers Documents > Compromis."
    ]
  },
  {
    icon: CalendarDays,
    title: "Calendrier",
    color: "text-indigo-500",
    description: "RDV liés aux projets",
    steps: [
      "Crée un RDV : titre, date, heure début/fin, type (Visite, Notaire, Banque, Artisan, Autre).",
      "Associe à un bien immobilier optionnellement.",
      "Code couleur par type. Vue mensuelle + clic sur jour pour lister les RDV."
    ]
  },
  {
    icon: Bell,
    title: "Alertes immobilières",
    color: "text-yellow-500",
    description: "Scan automatique 7 plateformes + webhook Make.com",
    steps: [
      "Crée une alerte : nom, ville (autocomplete CP), rayon (5-100 km), types de bien (multi), fourchette prix, surface mini.",
      "Sélectionne les plateformes : LeBonCoin, SeLoger, IAD, PAP, Century21, Orpi, Coup de Cœur.",
      "Bouton 'Rechercher' → scan + résultats affichés directement dans l'alerte.",
      "Liens directs 'Ouvrir sur LeBonCoin / SeLoger / etc.' avec critères pré-remplis.",
      "MIEUX : utilise le webhook Make.com (Paramètres > Webhook alertes) pour recevoir directement les annonces qui arrivent par email — chaque annonce devient une fiche bien automatiquement avec notification push."
    ],
    notes: [
      { kind: 'new', text: <><strong>Webhook Make.com (NEW) :</strong> configure tes alertes mail LeBonCoin/SeLoger/PAP/IAD vers une boîte Gmail dédiée → scénario Make.com gratuit (1000 ops/mois) qui parse l'email et POST vers ton URL webhook → la fiche apparaît dans <em>Recherche &gt; Auto-importées</em> avec un badge violet "NOUVEAU" et une notif. Zéro scraping, 100% légal. Voir Paramètres pour l'URL et la clé.</> }
    ]
  },
  {
    icon: Users,
    title: "Carnet d'artisans",
    color: "text-pink-500",
    description: "Contacts par corps de métier + accès portail",
    steps: [
      "Ajoute un artisan : nom, métier (12 corps), téléphone, email, ville.",
      "Tarif horaire + note qualité (1-5 étoiles) + notes (spécialités, dispo).",
      "Filtres par métier ou recherche texte.",
      "Bouton 'Créer accès' → crée le compte rôle 'artisan' lié à la fiche (voir Espace Artisan).",
      "Ces artisans peuvent être assignés directement dans les BDC et le Suivi Travaux."
    ]
  },
  {
    icon: Banknote,
    title: "Dossier de financement bancaire",
    color: "text-emerald-400",
    description: "PDF 18+ pages prêt à transmettre au banquier",
    steps: [
      "Page dédiée 'Dossier de financement' dans la sidebar : liste automatiquement tous les projets ayant au moins une offre d'achat sur le bien.",
      "Carte visuelle par projet : photo, statut pipeline, prix de référence (offre acceptée prioritaire), nombre d'offres, badge ✓ si offre acceptée.",
      "🖱️ Clic sur une carte = téléchargement direct du PDF dossier de financement (~1 MB).",
      "📚 Structure du PDF (généré à la demande, données live) :",
      "Page 1 — Page de garde : titre du dossier, nom du bien, adresse, prix, signature visuelle.",
      "Page 2 — Sommaire : table des matières automatique avec les vrais numéros de page de chaque section (régénéré à chaque export, 2 passes de rendu).",
      "Section 1 — Synthèse exécutive : statut du projet, type d'opération, régime TVA, mode de financement, capital sollicité, durée de portage, seuil de rentabilité.",
      "Section 2 — Présentation du bien : adresse, type, surface, pièces, prix affiché vs prix accepté (avec économie de négociation calculée), prix au m², DPE, source de l'annonce.",
      "Section 3 — Plan de financement : tableau EMPLOIS / RESSOURCES équilibré (les frais bancaires sont inclus dans le capital emprunté → EMPLOIS = RESSOURCES automatiquement).",
      "Section 3b — Échéancier prévisionnel du prêt (in fine) : caractéristiques du prêt + tableau mois par mois avec capital restant dû, intérêts, assurance, mensualité, cumul intérêts, jusqu'au remboursement à la revente. Limité à 24 mois affichés max.",
      "Section 4 — Étude de rentabilité : coût total acquisition, plus-value brute & nette, ROI brut & net, coûts de portage prévisionnels.",
      "Section 5 — Travaux (si visite technique disponible) : récap par corps de métier + verdict GO/ATTENTION/NO GO.",
      "Section 6 — Bons de commande artisans (BDC validés > 0 € uniquement, les BDC à 0 € sont automatiquement filtrés) : un BDC par page en format devis pro, avec bloc CLIENT/Maître d'ouvrage utilisant le Profil emprunteur (Nom Prénom + Téléphone perso) — les lignes à 0 € sont masquées pour la lisibilité.",
      "Section 7 — Cadastre / PLU : parcelle, zonage urbanistique, conditions d'urbanisme.",
      "Section 8 — Analyse des risques : risques majeurs identifiés + historique des arrêtés CatNat (date, nature, code JO) + zone sismique.",
      "Section 9 — Profil de l'emprunteur : identité civile, employeur, revenus annuels (saisis dans Paramètres).",
      "Section 10 — Pièces jointes annoncées : checklist 18 pièces à fournir (K-bis, RIB, bilans N-1/N-2, attestation comptable, compromis, devis signés, CNI dirigeant, justificatifs apport...) avec marquage ■ indispensable / □ recommandé.",
      "Page finale — Engagement et signature : déclaration de sincérité + cadre de signature.",
      "📄 Pied de page systématique sur toutes les pages : 'Document confidentiel — usage interne établissement bancaire' à gauche + 'Page X / Y' à droite."
    ],
    notes: [
      { kind: 'success', text: <><strong>Ce qui fait la différence :</strong> aucune resaisie. Le PDF est généré à la volée à partir des données réelles (étude, visite, BDC validés, profil emprunteur, analyse marché). Mettez à jour une donnée → re-téléchargez le PDF, tout est rafraîchi.</> },
      { kind: 'info', text: <><strong>Bon usage :</strong> téléchargez le PDF chaque fois que vous avez un RDV banque/courtier. Vous arrivez avec un dossier 18+ pages structuré, sourcé (APIs gouvernementales) et chiffré au centime — vous prouvez votre sérieux et votre maîtrise.</> }
    ]
  },
  {
    icon: FileText,
    title: "Documents",
    color: "text-teal-500",
    description: "GED par projet et étape",
    steps: [
      "Upload : photos, devis, factures, compromis, actes... Assigne directement à un projet + étape.",
      "Étapes : Prospection, Étude, Compromis, Achat, Travaux, Vente.",
      "Filtres par projet. Survol → Télécharger / Assigner / Supprimer.",
      "Les factures liées aux dépenses du Suivi Travaux apparaissent ici automatiquement.",
      "Boutons 'Compromis' / 'Acte' depuis le Pipeline et les Offres uploadent ici directement avec classement auto."
    ]
  },
  {
    icon: WifiOff,
    title: "Mode hors-ligne (PWA)",
    color: "text-emerald-400",
    description: "Visites + BPU + Études fonctionnent sans réseau",
    steps: [
      "L'app fonctionne comme une PWA : installable sur ton téléphone (Chrome/Safari → 'Ajouter à l'écran d'accueil').",
      "Hors ligne tu peux : créer/modifier des Visites, ajouter des photos (compressées localement à ~1MB), consulter le BPU, faire une Étude de rentabilité (calcul local).",
      "Bandeau orange en haut quand offline ou si des modifications attendent la sync.",
      "Sync automatique au retour réseau : les modifications sont poussées au serveur, et tu récupères ce que d'autres collaborateurs ont fait via un pull différentiel.",
      "Conflits (rare, ex : 2 personnes éditent la même visite hors-ligne) : visibles dans Paramètres > Conflits de synchro avec preview du payload."
    ],
    notes: [
      { kind: 'warn', text: <><strong>Bon réflexe :</strong> avant de partir sur le terrain en zone blanche, passe sur Visites + Bordereau au moins une fois en wifi pour pré-charger les caches.</> }
    ]
  },
  {
    icon: ShieldCheck,
    title: "Paramètres société",
    color: "text-zinc-300",
    description: "Frais fixes injectés dans toutes les études",
    steps: [
      "Settings > 'Frais fixes société' : 27 champs paramétrables.",
      "Assurances (décennale, RC pro, garantie financière), comptable annuel, banque, véhicule (crédit + carburant + assurance), rémunération brute + charges sociales, prévoyance, mutuelle, formation, etc.",
      "Aperçu live : total mensuel + total annuel.",
      "Champ 'Nb projets parallèles' : permet de répartir la charge sur plusieurs études simultanées (par défaut imputé en quote-part au prorata)."
    ]
  },
  {
    icon: Settings,
    title: "Personnalisation",
    color: "text-zinc-400",
    description: "UI à ton goût + cache mobile",
    steps: [
      "Couleur d'accent (8 options).",
      "Widgets dashboard activables/désactivables.",
      "Réordonne la sidebar par drag & drop (toutes les pages incluses, BPU, BDC, Dossier de financement, etc.).",
      "Bouton 'Vider le cache et recharger' (utile si la version mobile bloque sur une vieille version).",
      "Cache buster automatique : toast 'Nouvelle version' avec bouton Rafraîchir quand le serveur publie une mise à jour."
    ]
  },
  {
    icon: ShieldCheck,
    title: "Profil emprunteur (privé)",
    color: "text-gold-500",
    description: "Données personnelles pour le dossier banque",
    steps: [
      "Paramètres > carte 'Profil emprunteur' — données strictement privées, jamais partagées avec les autres utilisateurs du workspace.",
      "Identité civile : prénom, nom, date de naissance, situation matrimoniale.",
      "Coordonnées personnelles : téléphone perso, adresse perso, code postal, ville (utilisées dans le bloc 'CLIENT — MAÎTRE D'OUVRAGE' des BDC pour signer les bons de commande artisan).",
      "Situation pro : employeur, poste occupé, type de contrat (CDI / CDD / Indépendant / Dirigeant), date d'entrée.",
      "Revenus : revenu net annuel (salaire + dividendes), revenus fonciers, autres revenus. Sert au banquier à calculer le taux d'endettement et la capacité d'emprunt.",
      "Charges récurrentes : crédits en cours, pensions versées, loyer perso (si pas propriétaire de sa résidence).",
      "Patrimoine : épargne, valeur RP, autres biens immobiliers, parts sociales.",
      "Toutes ces données sont automatiquement injectées dans la Section 9 du dossier de financement bancaire."
    ],
    notes: [
      { kind: 'success', text: <><strong>Sécurité :</strong> le profil emprunteur est stocké isolément par workspace_id. Aucun collaborateur, même admin, ne peut le voir. Seul VOUS (l'utilisateur connecté) avez accès en lecture/écriture à votre propre profil.</> }
    ]
  }
];

export const HelpPage = () => {
  return (
    <div className="space-y-6 animate-fadeIn max-w-4xl pb-12" data-testid="help-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight">Guide d'utilisation</h1>
        <p className="text-zinc-400 mt-1">Tout sur Home MDB 2A — modules, flux, et changements récents</p>
      </div>

      {/* What's new */}
      <Card className="bg-gradient-to-br from-emerald-500/10 to-amber-500/5 border-emerald-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-400" />
            Quoi de neuf <Badge className="bg-emerald-500/30 text-emerald-200 border-0">Février 2026</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-zinc-300">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span><strong className="text-emerald-300">🏦 Dossier de financement bancaire automatisé</strong> — nouvelle page dédiée dans la sidebar listant tous les projets ayant une offre d'achat. PDF 18+ pages avec sommaire dynamique, échéancier de prêt in fine, plan de financement équilibré, analyse cadastre/PLU/risques/DVF, BDC artisans (les 0 € filtrés), profil emprunteur, checklist 18 pièces à fournir. Pagination "X / Y" + mention confidentielle sur toutes les pages.</span>
          </div>
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span><strong className="text-emerald-300">📍 Autocomplétion d'adresse BAN</strong> — saisie du bien avec autocomplétion type Google Maps (Base Adresse Nationale officielle). La ville, le code postal et les coordonnées GPS sont remplis automatiquement.</span>
          </div>
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span><strong className="text-emerald-300">🤖 Analyse marché AUTOMATIQUE</strong> — dès la création/modification d'un bien, 4 APIs gouvernementales (cadastre IGN, PLU GPU, risques Géorisques, ventes DVF Etalab) sont interrogées en arrière-plan. Plus besoin de cliquer manuellement, badge ✓ visible sur la carte du bien dans les 10 secondes.</span>
          </div>
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span><strong className="text-emerald-300">🗺️ Carte interactive Leaflet</strong> — sur chaque bien analysé, onglet "Carte" avec marqueur GPS + cercle de 250 m matérialisant la zone d'influence (commerces, transports, voisinage immédiat).</span>
          </div>
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span><strong className="text-emerald-300">📊 Suggestion DVF dans l'étude de rentabilité</strong> — carte verte qui s'affiche dans la section Revente quand l'analyse marché est disponible : prix m² moyen × surface du bien = prix de revente cible suggéré, avec écart % vs votre cible actuelle et bouton "Appliquer".</span>
          </div>
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span><strong className="text-emerald-300">⚠️ Risques détaillés (refonte)</strong> — Géorisques renvoie maintenant les nuances (mouvement de terrain, séisme, sécheresse, inondation, risque industriel...) + historique complet des arrêtés CatNat avec date, nature, code JO. Zone sismique 1-5 si applicable.</span>
          </div>
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span><strong className="text-emerald-300">📋 Profil emprunteur</strong> — nouvelle carte privée dans Paramètres (revenus, charges, employeur, adresse perso, patrimoine) — injecté automatiquement dans le bloc "Maître d'Ouvrage" des BDC et dans la Section 9 du dossier banque.</span>
          </div>
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span><strong className="text-emerald-300">💰 Plan de financement équilibré</strong> — les frais bancaires (dossier + courtage + garantie) sont désormais intégrés au capital sollicité → EMPLOIS = RESSOURCES automatiquement (fini l'écart de 1 000 € qui surprenait le banquier).</span>
          </div>
        </CardContent>
      </Card>

      {/* Quick start */}
      <Card className="bg-zinc-900 border-zinc-800 border-emerald-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-emerald-500" />
            Démarrage rapide — Le flux complet
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { num: 1, text: "Configure tes Frais fixes société + Profil emprunteur (Paramètres)", icon: ShieldCheck },
              { num: 2, text: "Construis ton BPU (Bordereau de prix MO) avec ton artisan", icon: BookOpen },
              { num: 3, text: "Ajoute un bien (Recherche) avec autocomplétion BAN — analyse marché auto-lancée", icon: Search },
              { num: 4, text: "Fais une Étude (suggestion DVF appliquée en 1 clic, projet créé auto en Prospection)", icon: Calculator },
              { num: 5, text: "Visite technique → autocomplete BPU + photos", icon: ClipboardCheck },
              { num: 6, text: "Envoie une offre → projet passe en 'En cours d'achat' auto", icon: Handshake },
              { num: 7, text: "Compromis signé → upload depuis Pipeline + statut auto", icon: FileText },
              { num: 8, text: "Télécharge le Dossier de financement bancaire (18+ pages, données live)", icon: Banknote },
              { num: 9, text: "BDC auto-générés par corps de métier → l'artisan saisit ses qtés", icon: ClipboardList },
              { num: 10, text: "Suivi Travaux : factures → dépenses + travaux_réels live", icon: Wrench },
              { num: 11, text: "Vendu → plus-value réelle calculée + clôture PDF", icon: Star },
            ].map(({ num, text, icon: Icon }) => (
              <div key={num} className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{num}</div>
                <div className="flex items-center gap-2 text-sm text-zinc-300 pt-1.5">
                  <Icon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  {text}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
            <p className="text-sm text-emerald-400">
              <strong>Tout est automatisé :</strong> les modules se parlent. Modifier une visite met à jour les BDC non-validés. Modifier un BPU met à jour les BDC qty=0. Une facture sur un BDC alimente le suivi travaux. Aucune resaisie nécessaire.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Detail par module */}
      <Accordion type="multiple" className="space-y-2">
        {sections.map((section, idx) => {
          const Icon = section.icon;
          return (
            <AccordionItem key={idx} value={`section-${idx}`} className="border border-zinc-800 rounded-lg overflow-hidden">
              <AccordionTrigger className="px-5 py-4 bg-zinc-900 hover:bg-zinc-800 hover:no-underline" data-testid={`help-trigger-${idx}`}>
                <div className="flex items-center gap-3 flex-1">
                  <Icon className={`w-5 h-5 ${section.color}`} />
                  <div className="text-left">
                    <span className="text-white font-medium">{section.title}</span>
                    <p className="text-xs text-zinc-500 mt-0.5">{section.description}</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="bg-zinc-900/50 px-5 py-4">
                <div className="space-y-3">
                  {section.steps.map((step, sIdx) => (
                    <Step key={sIdx} num={sIdx + 1}>{step}</Step>
                  ))}
                  {(section.notes || []).map((n, nIdx) => (
                    <div key={`n-${nIdx}`} className="mt-3">
                      <Note kind={n.kind}>{n.text}</Note>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Tips */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            Astuces pro
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {[
            "Pré-charge les caches BPU + Visite avant de partir en zone blanche : ouvre Bordereau et Visites au moins 1 fois en wifi.",
            "Utilise les badges cliquables sur une fiche bien (Étude / Visite / Projet / Offre) pour naviguer entre les modules.",
            "Le PDF Pro de Visite est ton meilleur outil de négociation : verdict GO/ATTENTION/NO GO + chiffrage par corps d'état + photos = un agent immobilier ne peut pas contredire les chiffres.",
            "Le bouton '🚀 Lancer ce projet' sur l'étude apparaît uniquement quand le projet est viable (PV nette > 0 ET ROI net ≥ 10%) — c'est ton garde-fou anti-coup-de-cœur.",
            "Configure tes Frais fixes société une fois pour toutes : ils seront imputés automatiquement à toutes tes études futures via la quote-part.",
            "🆕 Sois TOUJOURS précis sur l'adresse à la création d'un bien (numéro + rue, via autocomplétion BAN) : c'est ce qui rend l'analyse marché (cadastre / DVF / risques) fiable.",
            "🆕 Lance un dossier de financement EN AMONT du RDV banque : tu arrives avec un PDF 18+ pages sourcé (APIs officielles), chiffré au centime, avec checklist des pièces à fournir — le banquier voit immédiatement que tu maîtrises ton sujet.",
            "🆕 Profil emprunteur : remplis-le UNE FOIS, propre, complet (revenus, charges, patrimoine). Il alimente automatiquement la Section 9 du PDF banque et le bloc Maître d'Ouvrage des BDC.",
            "🆕 Suggestion DVF dans l'étude : utilise-la comme garde-fou. Si tu vises +20% au-dessus du marché, le banquier le verra et challengera. Reste à ±10% du prix m² moyen pour rester crédible.",
          ].map((tip, i) => (
            <div key={i} className="flex items-start gap-2 p-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-zinc-300 leading-relaxed">{tip}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpPage;
